import type { RJSFSchema } from '@rjsf/utils';
import dayjs from 'dayjs';

import { getCountryByName } from '@luxuryescapes/lib-countries';

import { PROPERTY_CHANNEL_MANAGERS } from '~/consts/reservation';

import type {
  OpportunityRelatedDeal,
  OpportunityRelatedExtraGuestSurcharge,
} from '~/hooks/apis/offers/useGetOpportunityRelated';

import { splitSalesforceMultiSelect } from '~/utils/splitSalesforceMultiSelect';

import type { AppliedFlashRoomRate } from './types';

export type VendorBillingDetails = {
  billingcity?: string | null;
  billingcountry?: string | null;
  billingpostalcode?: string | null;
  billingstate?: string | null;
  billingstreet?: string | null;
  name?: string | null;
};

type GetMissingRequiredPropertyFieldsParams = {
  isCreatePropertyStep: boolean;
  propertyFormData: Record<string, unknown>;
  propertySchema: RJSFSchema | undefined;
};

type GetNextButtonLabelParams = {
  isAdvancingStep?: boolean;
  isCreatePropertyStep: boolean;
  isCreatingNewProperty: boolean;
  isCreatingRooms?: boolean;
  roomCreateCount?: number;
  isRoomStep?: boolean;
  isValidatingOpportunity: boolean;
};

type FlashRoomRateAssignment = {
  packageData: Record<string, unknown>;
  packageId: string;
};

type RelatedPackageRoom = Pick<OpportunityRelatedDeal, 'id' | 'room_asset' | 'sfid'>;

// Defaults are based on averaged age ranges aggregated from svc-reservation data.
const DEFAULT_CHILD_MAX_AGE = 11;
const DEFAULT_INFANT_MAX_AGE = 1;

const REDEMPTION_OPTION_CHANNEL_MANAGERS = [
  { label: 'SiteMinder', channelManager: PROPERTY_CHANNEL_MANAGERS.siteminder },
  { label: 'Travelclick', channelManager: PROPERTY_CHANNEL_MANAGERS.travelclick },
  { label: 'Synxis', channelManager: PROPERTY_CHANNEL_MANAGERS.synxis },
  { label: 'Derbysoft', channelManager: PROPERTY_CHANNEL_MANAGERS.derbysoft },
  { label: 'Ratedock', channelManager: PROPERTY_CHANNEL_MANAGERS.ratedock },
  { label: 'Rentals United', channelManager: PROPERTY_CHANNEL_MANAGERS.rentalsunited },
  { label: 'PrimalRes', channelManager: PROPERTY_CHANNEL_MANAGERS.primalres },
] as const;

function normalizeRedemptionOption(value: string) {
  return value.trim().toLowerCase().replace(/\s+/g, '');
}

function parseAgeRangeMax(value: string | null | undefined) {
  const ages = value?.match(/\d+(?:\.\d+)?/g)?.map(Number);

  return ages?.length ? Math.floor(Math.max(...ages)) : undefined;
}

function normalizeAddressValue(value: string) {
  return value.toLowerCase().replace(/[^a-z0-9]/g, '');
}

const NUMBER_WORDS_TO_DIGITS: Record<string, string> = {
  zero: '0',
  one: '1',
  two: '2',
  three: '3',
  four: '4',
  five: '5',
  six: '6',
  seven: '7',
  eight: '8',
  nine: '9',
  ten: '10',
};

export function normaliseRoomName(name: string | null | undefined) {
  let normalized = (name ?? '').trim().toLowerCase().replace(/[-_]+/g, ' ');

  for (const [word, digit] of Object.entries(NUMBER_WORDS_TO_DIGITS)) {
    normalized = normalized.replace(new RegExp(`\\b${word}\\b`, 'g'), digit);
  }

  return normalized.replace(/\s+/g, ' ').trim();
}

export function roomNamesMatch(nameA: string | null | undefined, nameB: string | null | undefined) {
  const normalizedA = normaliseRoomName(nameA);
  const normalizedB = normaliseRoomName(nameB);

  if (!normalizedA || !normalizedB) {
    return false;
  }

  return normalizedA === normalizedB || normalizedA.includes(normalizedB) || normalizedB.includes(normalizedA);
}

export function getPackagePersistId(offerPackage: App.Package) {
  const persistId = offerPackage.id_salesforce_external ?? offerPackage.le_package_id;

  return persistId ? String(persistId) : undefined;
}

export function getOfferPackageName(offerPackage: App.Package) {
  return offerPackage.deal_option_name || offerPackage.name || getPackagePersistId(offerPackage) || 'Unknown package';
}

export function getPackageNumberOfNights(offerPackage: App.Package) {
  return 'number_of_nights' in offerPackage && typeof offerPackage.number_of_nights === 'number'
    ? offerPackage.number_of_nights
    : undefined;
}

export function sortPackagesByLengthOfStay(packages: Array<App.Package>) {
  return [...packages].sort((a, b) => {
    const nightsA = getPackageNumberOfNights(a) ?? Number.POSITIVE_INFINITY;
    const nightsB = getPackageNumberOfNights(b) ?? Number.POSITIVE_INFINITY;

    return nightsA - nightsB || getOfferPackageName(a).localeCompare(getOfferPackageName(b));
  });
}

// ApiError's own `message` is often a generic "Validation error" — the field-specific reason
// (e.g. "A flexible nights package requires a max extra nights value") lives in `errors`.
export function getPackageUpdateErrorDetail(error: unknown, fallbackMessage: string) {
  if (
    error &&
    typeof error === 'object' &&
    'errors' in error &&
    Array.isArray(error.errors) &&
    error.errors.length > 0
  ) {
    return error.errors
      .map((issue) => (typeof issue === 'string' ? issue : issue?.message))
      .filter(Boolean)
      .join('; ');
  }

  return fallbackMessage;
}

export function buildFlashPackageRoomRateAssignments({
  offerPackages = [],
  propertyId,
  relatedPackageRooms = [],
  roomRates,
  roomTypes,
}: {
  offerPackages?: Array<App.Package>;
  propertyId: string;
  relatedPackageRooms?: Array<RelatedPackageRoom>;
  roomRates: Array<AppliedFlashRoomRate>;
  roomTypes: Array<Pick<App.RoomType, 'id' | 'name'>>;
}): { assignments: Array<FlashRoomRateAssignment>; unmatchedPackageNames: Array<string> } {
  const roomRatesByRoomTypeId = new Map<string, Array<AppliedFlashRoomRate>>();

  roomRates.forEach((roomRate) => {
    const existingRoomRates = roomRatesByRoomTypeId.get(roomRate.roomTypeId) ?? [];
    roomRatesByRoomTypeId.set(roomRate.roomTypeId, existingRoomRates.concat(roomRate));
  });

  const roomsWithRates = roomTypes.filter((roomType) => roomRatesByRoomTypeId.has(roomType.id));
  const relatedRoomByPackageId = new Map(
    relatedPackageRooms.flatMap((relatedRoom) => {
      const packageId = relatedRoom.sfid || relatedRoom.id;
      return packageId && relatedRoom.room_asset?.name ? [[String(packageId), relatedRoom.room_asset.name]] : [];
    }),
  );

  const assignments: Array<FlashRoomRateAssignment> = [];
  const unmatchedPackageNames: Array<string> = [];

  offerPackages.forEach((offerPackage) => {
    const packageId = getPackagePersistId(offerPackage);
    const roomAssetName = packageId ? relatedRoomByPackageId.get(packageId) : undefined;
    const roomType = roomsWithRates.find((roomType) => roomNamesMatch(roomType.name, roomAssetName));
    const roomRatesForRoom = roomType ? (roomRatesByRoomTypeId.get(roomType.id) ?? []) : [];

    if (!packageId || !roomType || roomRatesForRoom.length === 0) {
      const reason = !packageId
        ? 'missing a persist id'
        : !roomAssetName
          ? `no related room found for package id ${packageId}`
          : `no room type named "${roomAssetName}" has an applied rate`;

      unmatchedPackageNames.push(`${getOfferPackageName(offerPackage)} (${reason})`);
      return;
    }

    if (roomRatesForRoom.length > 1) {
      assignments.push({
        packageId,
        packageData: {
          fk_property_id: propertyId,
          fk_room_type_id: roomType.id,
        },
      });
      return;
    }

    const [roomRate] = roomRatesForRoom;

    assignments.push({
      packageId,
      packageData: {
        fk_property_id: propertyId,
        fk_room_type_id: roomType.id,
        fk_room_rate_id: roomRate.roomRateId,
      },
    });
  });

  return { assignments, unmatchedPackageNames };
}

export function getFlashRatePlanNameFromTravelStart(deals: Array<OpportunityRelatedDeal> = []) {
  const travelStarts = deals
    .map((deal) => deal.travel_start_date__c)
    .filter((value): value is string => !!value)
    .map((value) => dayjs(value))
    .filter((date) => date.isValid())
    .sort((a, b) => a.valueOf() - b.valueOf());

  if (travelStarts.length === 0) {
    return '';
  }

  return formatFlashRatePlanName(travelStarts[0].format('YYYY-MM-DD'));
}

export function formatFlashRatePlanName(date: Date | string) {
  return `Flash ${dayjs(date).format('MMM YY')}`;
}

function hasAddressValue(addressParts: Array<string>, value: string, countryCode?: string) {
  const normalizedValue = normalizeAddressValue(value);
  const normalizedCountryCode = countryCode?.toLowerCase();

  return addressParts.some((part) => {
    const normalizedPart = normalizeAddressValue(part);
    return (
      normalizedPart.includes(normalizedValue) ||
      normalizedValue.includes(normalizedPart) ||
      (normalizedCountryCode === 'us' && normalizedPart === 'usa') ||
      (!!normalizedCountryCode && normalizedPart === normalizedCountryCode)
    );
  });
}

export function mapRedemptionOptionsToPropertyChannelManager(redemptionOptions?: string | null) {
  if (!redemptionOptions?.trim()) {
    return PROPERTY_CHANNEL_MANAGERS.selfManaged;
  }

  const normalizedMappings = REDEMPTION_OPTION_CHANNEL_MANAGERS.map(({ label, channelManager }) => ({
    channelManager,
    normalizedLabel: normalizeRedemptionOption(label),
  }));

  const options = splitSalesforceMultiSelect(redemptionOptions);

  for (const option of options) {
    const normalizedOption = normalizeRedemptionOption(option);
    const match = normalizedMappings.find(({ normalizedLabel }) => normalizedLabel === normalizedOption);

    if (match) {
      return match.channelManager;
    }
  }

  return PROPERTY_CHANNEL_MANAGERS.selfManaged;
}

export function mapRedemptionOptionsToPropertyFormData(redemptionOptions?: string | null): Record<string, string> {
  return {
    channel_manager: mapRedemptionOptionsToPropertyChannelManager(redemptionOptions),
  };
}

export function mapExtraGuestSurchargeAgeRangesToPropertyFormData(
  extraGuestSurcharges: Array<OpportunityRelatedExtraGuestSurcharge> = [],
): Record<string, number | string> {
  let childMaxAge: number | undefined;
  let infantMaxAge: number | undefined;

  extraGuestSurcharges.forEach((surcharge) => {
    const ageRangeMax = parseAgeRangeMax(surcharge.age_range__c);
    const guestType = surcharge.extra_guest_type__c?.toLowerCase();

    if (ageRangeMax === undefined || !guestType) {
      return;
    }

    if (guestType.includes('child')) {
      childMaxAge = childMaxAge === undefined ? ageRangeMax : Math.max(childMaxAge, ageRangeMax);
    }

    if (guestType.includes('infant')) {
      infantMaxAge = infantMaxAge === undefined ? ageRangeMax : Math.max(infantMaxAge, ageRangeMax);
    }
  });

  const maxChildAge = childMaxAge ?? DEFAULT_CHILD_MAX_AGE;

  return {
    children_policy: `${maxChildAge} years & under`,
    max_child_age: maxChildAge,
    max_infant_age: infantMaxAge ?? DEFAULT_INFANT_MAX_AGE,
  };
}

export function mapVendorBillingToPropertyFormData(vendor?: VendorBillingDetails | null): Record<string, unknown> {
  const name = vendor?.name?.trim();
  const billingstreetRaw = vendor?.billingstreet?.trim();
  const fullAddressParts =
    billingstreetRaw
      ?.split(',')
      .map((part) => part.trim())
      .filter(Boolean) ?? [];
  const hasFullBillingstreet = fullAddressParts.length >= 5;
  const billingcity = vendor?.billingcity?.trim() || (hasFullBillingstreet ? fullAddressParts[1] : undefined);
  const billingcountry =
    vendor?.billingcountry?.trim() ||
    (hasFullBillingstreet ? fullAddressParts[fullAddressParts.length - 1] : undefined);
  const billingCountryCode = billingcountry ? getCountryByName(billingcountry)?.iso2 : undefined;
  const billingpostalcode =
    vendor?.billingpostalcode?.trim() ||
    (hasFullBillingstreet ? fullAddressParts[fullAddressParts.length - 2] : undefined);
  const billingstate =
    vendor?.billingstate?.trim() || (hasFullBillingstreet ? fullAddressParts[fullAddressParts.length - 3] : undefined);
  const addressParts = billingstreetRaw ? [billingstreetRaw] : [];
  const dedupeAddressParts = billingstreetRaw ? [...fullAddressParts] : [];
  [billingcity, billingstate, billingpostalcode, billingcountry].forEach((part) => {
    if (part && !hasAddressValue(dedupeAddressParts, part, part === billingcountry ? billingCountryCode : undefined)) {
      addressParts.push(part);
      dedupeAddressParts.push(part);
    }
  });
  const address = addressParts.join(', ');

  return {
    ...(name ? { name } : {}),
    ...(address ? { address } : {}),
    ...(billingcity ? { city: billingcity, location_heading: billingcity } : {}),
    ...(billingcountry ? { country: billingcountry, location_subheading: billingcountry } : {}),
  };
}

export function getMissingRequiredFieldLabels(schema: RJSFSchema | undefined, formData: Record<string, unknown>) {
  return (schema?.required ?? [])
    .filter((field) => {
      const value = formData[field];
      return value === undefined || value === null || value === '' || (Array.isArray(value) && value.length === 0);
    })
    .map((field) => {
      const propertySchema = schema?.properties?.[field];
      return typeof propertySchema === 'object' && propertySchema?.title ? propertySchema.title : field;
    });
}

export function getMissingRequiredPropertyFields({
  isCreatePropertyStep,
  propertyFormData,
  propertySchema,
}: GetMissingRequiredPropertyFieldsParams) {
  if (!isCreatePropertyStep) {
    return [];
  }

  return getMissingRequiredFieldLabels(propertySchema, propertyFormData);
}

export function getSaveStepButtonLabel(isSaving: boolean) {
  return isSaving ? 'Saving...' : 'Save';
}

export function getNextButtonLabel({
  isAdvancingStep,
  isCreatePropertyStep,
  isCreatingNewProperty,
  isCreatingRooms,
  isRoomStep,
  roomCreateCount = 0,
  isValidatingOpportunity,
}: GetNextButtonLabelParams) {
  if (isValidatingOpportunity) {
    return 'Checking...';
  }

  if (isAdvancingStep) {
    return 'Preparing rooms...';
  }

  if (isCreatingRooms) {
    return 'Creating rooms...';
  }

  if (isRoomStep && roomCreateCount > 0) {
    return `Create ${roomCreateCount} room${roomCreateCount === 1 ? '' : 's'} and next`;
  }

  if (!isCreatePropertyStep) {
    return 'Next';
  }

  if (isCreatingNewProperty) {
    return 'Creating...';
  }

  return 'Create property and next';
}
